import api from "../config/api";
import type { ApiResponse, PaginatedResponse } from "../types";

// Room Types
export interface Room {
  _id: string;
  roomNumber: string;
  roomName: string;
  roomType: 'single' | 'couple' | 'vip' | 'group';
  capacity: number;
  status: 'available' | 'occupied' | 'maintenance' | 'cleaning';
  amenities: string[];
  hourlyRate: number;
  createdAt: string;
  updatedAt: string;
}

// Request Interfaces
export interface CreateRoomRequest {
  roomNumber: string;
  roomName: string;
  roomType?: 'single' | 'couple' | 'vip' | 'group';
  capacity: number;
  status?: 'available' | 'occupied' | 'maintenance' | 'cleaning';
  amenities?: string[];
  hourlyRate?: number;
}

export interface UpdateRoomRequest extends Partial<CreateRoomRequest> {}

export interface UpdateRoomStatusRequest {
  status: 'available' | 'occupied' | 'maintenance' | 'cleaning';
}

export interface GetRoomsParams {
  page?: number;
  limit?: number;
  roomType?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface RoomAvailability {
  _id: string;
  roomNumber: string;
  roomName: string;
  roomType: string;
  status: string;
  isAvailable: boolean;
  currentBooking?: {
    startTime: string;
    endTime: string;
    customer: string;
  };
}

// Room Service
export const roomService = {
  // ดึงรายการห้องทั้งหมด
  getRooms: async (params: GetRoomsParams = {}): Promise<PaginatedResponse<Room>> => {
    const response = await api.get('/rooms', { params });
    return response.data;
  },

  // ดึงข้อมูลห้องตาม ID
  getRoom: async (id: string): Promise<ApiResponse<Room>> => {
    const response = await api.get(`/rooms/${id}`);
    return response.data;
  },

  // สร้างห้องใหม่
  createRoom: async (data: CreateRoomRequest): Promise<ApiResponse<Room>> => {
    const response = await api.post('/rooms', data);
    return response.data;
  },

  // ແກ້ໄຂข้อมูลห้อง
  updateRoom: async (id: string, data: UpdateRoomRequest): Promise<ApiResponse<Room>> => {
    const response = await api.put(`/rooms/${id}`, data);
    return response.data;
  },

  // ลบห้อง
  deleteRoom: async (id: string): Promise<ApiResponse<Room>> => {
    const response = await api.delete(`/rooms/${id}`);
    return response.data;
  },

  // ແກ້ໄຂสถานะห้อง
  updateRoomStatus: async (id: string, data: UpdateRoomStatusRequest): Promise<ApiResponse<Room>> => {
    const response = await api.put(`/rooms/${id}/status`, data);
    return response.data;
  },

  // ตรวจสอบความพร้อมใช้งานของห้องตามวันที่
  getRoomAvailability: async (date: string): Promise<ApiResponse<RoomAvailability[]>> => {
    const response = await api.get(`/rooms/availability/${date}`);
    return response.data;
  },

  // Helper methods สำหรับใช้งานเฉพาะ
  
  // ดึงห้องที่ว่างเท่านั้น
  getAvailableRooms: async (params: GetRoomsParams = {}): Promise<PaginatedResponse<Room>> => {
    return roomService.getRooms({ ...params, status: 'available' });
  },

  // ดึงห้องตามประเภท
  getRoomsByType: async (roomType: string, params: GetRoomsParams = {}): Promise<PaginatedResponse<Room>> => {
    return roomService.getRooms({ ...params, roomType });
  },

  // ค้นหาห้อง
  searchRooms: async (search: string, params: GetRoomsParams = {}): Promise<PaginatedResponse<Room>> => {
    return roomService.getRooms({ ...params, search });
  },

  // เปลี่ยนสถานะห้องเป็นว่าง
  setRoomAvailable: async (id: string): Promise<ApiResponse<Room>> => {
    return roomService.updateRoomStatus(id, { status: 'available' });
  },

  // เปลี่ยนสถานะห้องเป็นไม่ว่าง
  setRoomOccupied: async (id: string): Promise<ApiResponse<Room>> => {
    return roomService.updateRoomStatus(id, { status: 'occupied' });
  },

  // เปลี่ยนสถานะห้องเป็นซ่อมแซม
  setRoomMaintenance: async (id: string): Promise<ApiResponse<Room>> => {
    return roomService.updateRoomStatus(id, { status: 'maintenance' });
  },

  // เปลี่ยนสถานะห้องเป็นทำความสะอาด
  setRoomCleaning: async (id: string): Promise<ApiResponse<Room>> => {
    return roomService.updateRoomStatus(id, { status: 'cleaning' });
  }
};